/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useImperativeHandle, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { Button } from '@mui/material';
import { addClass } from '@grapecity/wijmo';
import { CellType } from '@grapecity/wijmo.grid';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import { findElectricSummary } from 'apis/ut/UtMatrixRegist';
import { UtMatrixDetail, UtMatrixRegistSearchCondition } from 'models/ut/UtMatrix';
import { hasRole } from 'utils/SessionUtil';
import { getExcelFileName } from 'utils/ExcelUtil';
import { DefaultZeroNumberCellTemplate } from 'components/grids/NumberCellRenderer';

/**
 * @desc 검토/결재 > [전기요약] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.01   jshwang12       자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리)
 * 2026.01.26   jihwan89        CSR E20260106-000678 검토화면 천단위 숫자 인식되게 변경(컴마삭제)
 */
const ElectricSummaryContent = (props: any, ref) => {
  const {
    onSubmit,
    condition = {} as UtMatrixRegistSearchCondition,
    processData = [] as UtMatrixDetail[],
  } = props;
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const [rowData, setRowData] = useState<UtMatrixDetail[]>([]);
  const [flexRef, setFlexRef] = useState<any>();
  const [hasAuth, setHasAuth] = useState<boolean>(false);
  const defaultGridHeight = 277 + 30;

  const isAprText = useMemo(() => {
    const p = processData.filter((o) => o.utmWrtProcProgStatCd === 'UTP05');
    return condition.utmWkProgStatCd === 'UTM04' && p.length > 0
      ? String(t('com.button.결재정보', '결재정보'))
      : String(t('com.button.결재요청', '결재요청'));
  }, [processData, condition.utmWkProgStatCd]);

  // 2025.09.16 sungim.kim2 검토완료 <재작성요청><보류><결재요청>
  const isUtReviewComplete = useMemo(() => {
    return condition.utmWkProgStatCd === 'UTM07' && condition.openMode !== 'appr' && hasAuth;
  }, [condition.utmWkProgStatCd, hasAuth]);

  //검토중
  const isUtReview = useMemo(() => {
    return condition.utmWkProgStatCd === 'UTM04' && condition.openMode !== 'appr' && hasAuth;
  }, [condition.utmWkProgStatCd, hasAuth]);

  // 작성완료 : <검토진행> <재작성요청> <보류> <목록> <다운로드>
  const isUtComplete = useMemo(() => {
    const p = processData.filter((o) => o.utmWrtProcProgStatCd === 'UTP03');

    return condition.utmWkProgStatCd === 'UTM03' && hasAuth;
  }, [processData, condition.utmWkProgStatCd, hasAuth]);

  // 작성중 : <검증> <임시저장> <새로고침>
  const isUtWriter = useMemo(() => {
    const p = processData.filter((o) => o.utmWrtProcProgStatCd === 'UTP02');
    return condition.utmWkProgStatCd === 'UTM02' && hasAuth; //작성중
  }, [processData, condition.utmWkProgStatCd, hasAuth]);

  useImperativeHandle(ref, () => ({
    searchElectricSummary: (condition) => {
      searchElectricSummary(condition);
    },
  }));

  useEffect(() => {
    if (hasRole('ADM') || hasRole('UT_MANAGER')) {
      setHasAuth(true);
    }
  }, []);

  const searchElectricSummary = (condition) => {
    findElectricSummary(condition).then((result) => {
      setRowData(result);
    });
  };

  const layoutDefinition = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      width: 40,
      align: 'center',
      cellTemplate: (grid) => grid.row._idx + 1,
    },
    {
      binding: 'prdnProcCdNm',
      header: String(t('ut.label.공정', '공정')),
      minWidth: 120,
      width: '*',
      align: 'center',
    },
    {
      binding: 'eqpPrdnLnQty',
      header: String(t('ut.label.라인수량', '라인수량')),
      minWidth: 120,
      width: '*',
    },
    {
      binding: 'vltgNvl',
      header: String(t('ut.label.전압', '전압')),
      align: 'center',
      minWidth: 120,
      width: '*',
    },
    {
      binding: 'elpwEqpQty',
      header: String(t('ut.label.설비수량', '설비수량')),
      align: 'right',
      minWidth: 120,
      width: '*',
      cellTemplate: DefaultZeroNumberCellTemplate,
    },
    {
      binding: 'od2EcNvl',
      header: String(t('ut.label.수용율 적용전', '수용율 적용전')),
      align: 'right',
      minWidth: 120,
      width: '*',
      cellTemplate: DefaultZeroNumberCellTemplate,
    },
    {
      binding: 'elecDmdRate',
      header: String(t('ut.label.수용율 적용 후', '수용율 적용 후')),
      align: 'right',
      minWidth: 120,
      width: '*',
      cellTemplate: DefaultZeroNumberCellTemplate,
    },
    {
      binding: 'diff',
      header: String(t('ut.label.차이', '차이')),
      align: 'right',
      minWidth: 120,
      width: '*',
      cellTemplate: (params) =>
        `${((params.item.od2EcNvl || 0) - (params.item.elecDmdRate || 0)).toLocaleString()}`,
    },
  ];

  const handleSubmit = () => {
    props?.onSubmit?.();
  };

  //검토진행
  const handleSubmitReview = () => {
    props?.onSubmit?.();
  };

  //검증
  const handleModify = () => {
    props?.onModify?.();
  };
  //검토완료
  const handleSubmitReviewComplete = () => {
    props?.onSubmitComplete?.();
  };
  //보류 링크
  const handleRequestLinkHold = () => {
    props?.onLinkHold?.();
  };
  //재작성 링크
  const handleRequestLinkRewrite = () => {
    props?.onLinkRewrite?.();
  };

  const handleApproval = () => {
    props?.onAppr?.();
  };

  const btnExcelExportGrid = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
      formatItem: exportFormat,
    });
    book.sheets[0].name = 'Ut Matrix 검토 전기 요약';
    book.saveAsync(
      getExcelFileName(t('ut.label.Ut Matrix 검토 전기 요약', 'Ut Matrix 검토 전기 요약'))
    );
  };

  const exportFormat = (args) => {
    const xlsxCell = args.xlsxCell;
    const cell = args.getFormattedCell();
    // xlsxCell.value = cell.textContent;
    const delCommaValue = cell.textContent?.trim().replace(/,/g, '');
    xlsxCell.value = delCommaValue;
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if (binding) {
        if (item.vltgNvl === 'Sub Sum') {
          addClass(cell, 'rowTotal');
        } else if (item.vltgNvl === 'Total Sum') {
          addClass(cell, 'sumTotal');
        }
      }
    }
  });
  const onInitialized = (grid) => {
    setFlexRef(grid);
    grid.itemFormatter = onItemFormatter;
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.전기요약', '전기요약')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
          {condition?.utmNo && <div className="info warning">{condition?.utmNo}</div>}
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExportGrid}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => searchElectricSummary(condition)}
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={props?.isOpenCondition ? defaultGridHeight + 55 : defaultGridHeight + 60 + 196 + 69}
        isReadOnly={true}
        isSelector={false}
        frozenColumns={3}
        excludePin={['utmSeq']}
        excludeFilter={['utmSeq']}
        allowPinning={false}
        initialized={onInitialized}
      />
      <GlobalBtnGroup>
        {/* 작성중 : <검증> <임시저장> <새로고침> UTM02  */}
        {/* 작성완료 : <검토진행> <재작성요청> <보류> <목록> <다운로드>  UTM05 */}
        {/* 검토중 : <결재요청> <목록> <다운로드>  UTM04 */}
        {/* 그외 (결재중) : <목록> <다운로드>  */}

        {isUtWriter && (
          <>
            <Button css={IconButton.button} className="draft" onClick={handleModify}>
              {t('com.button.검증', '검증')}
            </Button>
            {/*<Button css={IconButton.button} className="draft" onClick={() => handleSave()}>*/}
            {/*  임시저장*/}
            {/*</Button>*/}
          </>
        )}
        {isUtComplete && (
          <>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkRewrite}>
              {t('com.button.재작성요청', '재작성요청')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkHold}>
              {t('com.button.보류', '보류')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleSubmitReview}>
              {t('com.button.검토진행', '검토진행')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleModify}>
              {t('com.button.검증', '검증')}
            </Button>
            {/*<Button css={IconButton.button} className="draft" onClick={() => handleSave()}>*/}
            {/*  임시저장*/}
            {/*</Button>*/}
          </>
        )}
        {isUtReview && (
          <>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkRewrite}>
              {t('com.button.재작성요청', '재작성요청')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkHold}>
              {t('com.button.보류', '보류')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleModify}>
              {t('com.button.검증', '검증')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleSubmitReviewComplete}>
              {t('com.button.검토완료', '검토완료')}
            </Button>
          </>
        )}

        {/*2025.09.16 sungim.kim2 검토완료 상태 일때 버튼 정의*/}
        {isUtReviewComplete && (
          // <재작성요청><보류><결재요청>
          <>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkRewrite}>
              {t('com.button.재작성요청', '재작성요청')}
            </Button>
            <Button css={IconButton.button} className="draft" onClick={handleRequestLinkHold}>
              {t('com.button.보류', '보류')}
            </Button>
            <Button css={IconButton.button} className="save" onClick={handleApproval}>
              {isAprText}
            </Button>
          </>
        )}
      </GlobalBtnGroup>
    </>
  );
};

export default React.forwardRef(ElectricSummaryContent);
